import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronDown, ChevronRight, Clock, Loader2, Wallet } from "lucide-react";
import {
  fetchPayoutOverview,
  markPayoutAsPaid,
  type PayoutLedgerRow,
  type PayoutLedgerStatus,
  type PayoutTenantOption,
} from "../../../lib/adminTools";
import { Button } from "../../ui/button";

export type PayoutIdentity = { uid?: string | null; email?: string | null } | null | undefined;

export type { PayoutLedgerRow } from "../../../lib/adminTools";

function money(value: number | null | undefined): string {
  if (value == null) return "—";
  return Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatusPill({ status }: { status: PayoutLedgerStatus }) {
  const styles: Record<PayoutLedgerStatus, string> = {
    "Current Month": "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300",
    "Pending Payment": "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300",
    "Paid": "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ${styles[status]}`}>
      {status === "Paid" ? <CheckCircle2 className="h-3 w-3" /> : status === "Pending Payment" ? <Clock className="h-3 w-3" /> : <Wallet className="h-3 w-3" />}
      {status}
    </span>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  // Stored paidAt timestamps are UTC calendar dates (e.g. 2026-06-30T00:00:00Z);
  // render the UTC date so the short date always matches the recorded payment date.
  return d.toLocaleDateString("en-US", { timeZone: "UTC", year: "numeric", month: "short", day: "numeric" });
}

export function PartnerPayoutsSection({ accountIdentity }: { accountIdentity: PayoutIdentity }) {
  const [tenants, setTenants] = useState<PayoutTenantOption[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>("");
  const [tenantName, setTenantName] = useState<string>("");
  const [history, setHistory] = useState<PayoutLedgerRow[]>([]);
  const [collapsedYears, setCollapsedYears] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState<PayoutLedgerRow | null>(null);

  const identity = useMemo(
    () => (accountIdentity?.uid ? { uid: String(accountIdentity.uid), email: accountIdentity.email || null } : null),
    [accountIdentity],
  );

  const load = useCallback(
    async (tenantSlug?: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchPayoutOverview(identity, tenantSlug);
        setTenants(Array.isArray(result.tenants) ? result.tenants : []);
        setSelectedTenant(result.tenantSlug || "");
        setTenantName(result.tenantName || "");
        setHistory(Array.isArray(result.history) ? result.history : []);
      } catch (e) {
        setError(String((e as Error)?.message || "Unable to load the payout ledger."));
      } finally {
        setLoading(false);
      }
    },
    [identity],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const handleTenantChange = (slug: string) => {
    if (!slug || slug === selectedTenant) return;
    setSelectedTenant(slug);
    void load(slug);
  };

  const years = useMemo(() => {
    const byYear = new Map<number, PayoutLedgerRow[]>();
    for (const row of history) {
      const year = Number(row.period.slice(0, 4));
      if (!Number.isFinite(year)) continue;
      if (!byYear.has(year)) byYear.set(year, []);
      byYear.get(year)!.push(row);
    }
    return Array.from(byYear.entries()).sort((a, b) => b[0] - a[0]);
  }, [history]);

  const toggleYear = (year: number) => {
    setCollapsedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  // The newest year is expanded by default; every older year starts collapsed.
  useEffect(() => {
    if (history.length === 0) return;
    const yearSet = new Set<number>();
    for (const row of history) {
      const year = Number(row.period.slice(0, 4));
      if (Number.isFinite(year)) yearSet.add(year);
    }
    if (yearSet.size === 0) return;
    yearSet.delete(Math.max(...Array.from(yearSet)));
    setCollapsedYears(yearSet);
  }, [history]);

  return (
    <div className="space-y-4">
      {error ? (
        <p className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">{error}</p>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-zinc-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading payout ledger…
        </div>
      ) : tenants.length === 0 ? (
        <p className="py-8 text-center text-sm text-zinc-400">No partner tenants configured yet.</p>
      ) : (
        <>
          <label className="relative -top-14 flex items-center justify-end gap-2 text-sm font-medium text-zinc-700 dark:text-zinc-200">
            Partner
            <span className="relative">
              <select
                aria-label="Partner"
                data-testid="payout-partner-select"
                className="partner-payout-select admin-tools-custom-chevron h-9 appearance-none rounded-lg border border-zinc-300 bg-white px-2.5 pr-8 text-sm text-zinc-900 dark:border-slate-700 dark:bg-slate-950 dark:text-zinc-100"
                value={selectedTenant}
                onChange={(event) => handleTenantChange(event.target.value)}
              >
                {tenants.map((t) => (
                  <option key={t.tenantSlug} value={t.tenantSlug}>
                    {t.tenantName}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500 dark:text-zinc-400" />
            </span>
          </label>

          {years.length === 0 ? (
            <div
              data-testid="payout-ledger-empty"
              className="relative -top-5 !mt-0 rounded-lg border border-dashed border-zinc-200 px-4 py-4 text-center text-sm text-zinc-400 dark:border-zinc-800 dark:bg-slate-900 dark:text-[#c3c3c6]"
            >
              No payout history for this partner yet. Closed months appear here once they are frozen in the payout ledger.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
              <table className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-400 dark:border-zinc-800">
                    <th className="px-4 py-3 font-semibold">Period</th>
                    <th className="px-4 py-3 font-semibold">Gross Revenue</th>
                    <th className="px-4 py-3 font-semibold">Partner Earnings</th>
                    <th className="px-4 py-3 font-semibold">TradeProof Revenue</th>
                    <th className="px-4 py-3 font-semibold">Payout Status</th>
                    <th className="px-4 py-3 font-semibold">Paid On</th>
                    <th className="px-4 py-3 font-semibold">Reference</th>
                    <th className="px-4 py-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {years.map(([year, rows]) => (
                    <LedgerYearGroup
                      key={year}
                      year={year}
                      rows={rows}
                      collapsed={collapsedYears.has(year)}
                      onToggle={() => toggleYear(year)}
                      onProcess={setMarking}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <MarkPaidDialog
        partner={marking}
        tenantName={tenantName}
        identity={identity ?? null}
        onClose={() => setMarking(null)}
        onSaved={() => {
          setMarking(null);
          void load(selectedTenant || undefined);
        }}
      />
    </div>
  );
}


function LedgerYearGroup({
  year,
  rows,
  collapsed,
  onToggle,
  onProcess,
}: {
  year: number;
  rows: PayoutLedgerRow[];
  collapsed: boolean;
  onToggle: () => void;
  onProcess: (row: PayoutLedgerRow) => void;
}) {
  return (
    <>
      <tr
        data-testid={`ledger-year-${year}`}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        className="cursor-pointer border-b border-zinc-200 bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500/50 dark:border-zinc-800 dark:bg-slate-900/60"
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <td colSpan={8} className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
          <span className="inline-flex items-center gap-1.5">
            {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {year}
          </span>
        </td>
      </tr>
      {!collapsed ? rows.map((row) => <LedgerRow key={row.period} row={row} onProcess={onProcess} />) : null}
    </>
  );
}

function LedgerRow({ row, onProcess }: { row: PayoutLedgerRow; onProcess: (row: PayoutLedgerRow) => void }) {
  return (
    <tr data-testid={`ledger-row-${row.period}`} className="border-b border-zinc-100 last:border-b-0 dark:border-zinc-800">
      <td className="px-4 py-3 font-medium text-zinc-900 dark:text-zinc-100">{row.periodLabel}</td>
      <td className="px-4 py-3 tabular-nums text-zinc-700 dark:text-zinc-300">${money(row.grossRevenue)}</td>
      <td className="px-4 py-3 tabular-nums text-zinc-700 dark:text-zinc-300">${money(row.partnerEarnings)}</td>
      <td className="px-4 py-3 tabular-nums text-zinc-700 dark:text-zinc-300">${money(row.tradeProofRevenue)}</td>
      <td className="px-4 py-3">
        <StatusPill status={row.status} />
      </td>
      <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">{row.paidOn ? formatDate(row.paidOn) : ""}</td>
      <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">{row.reference || ""}</td>
      <td className="px-4 py-3">
        {row.status === "Pending Payment" && row.payoutId ? (
          <Button size="sm" variant="secondary" onClick={() => onProcess(row)}>
            Mark Paid
          </Button>
        ) : null}
      </td>
    </tr>
  );
}


function MarkPaidDialog({
  partner,
  tenantName,
  identity,
  onClose,
  onSaved,
}: {
  partner: PayoutLedgerRow | null;
  tenantName: string;
  identity: { uid?: string | null; email?: string | null } | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [date, setDate] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  if (!partner) return null;

  const confirm = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      await markPayoutAsPaid(
        identity ? { uid: String(identity.uid ?? ""), email: identity.email ?? null } : null,
        {
          payoutId: partner.payoutId ?? "",
          paidAt: date ? new Date(`${date}T00:00:00`).toISOString() : new Date().toISOString(),
          paymentReference: reference.trim() || undefined,
          paymentNotes: notes.trim() || undefined,
        },
      );
      onSaved();
    } catch (e) {
      setFormError(String((e as Error)?.message || "Unable to record payment."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-6 shadow-2xl dark:border-gray-700 dark:bg-[#1a1a1c]"
        onClick={(e) => e.stopPropagation()}
        data-testid="mark-paid-dialog"
      >
        <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">Mark Paid</h3>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          Record a payment for {tenantName} · {partner.periodLabel}.
        </p>
        <div className="mt-3 flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900">
          <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Partner Earnings</span>
          <span className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">
            ${money(partner.partnerEarnings)}
          </span>
        </div>
        <p className="mt-2 text-xs text-zinc-400 dark:text-zinc-500">The payout amount is frozen and cannot change.</p>
        <div className="mt-4 space-y-3">
          <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-300">
            Payment Date
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900" />
          </label>
          <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-300">
            Reference / Transaction ID
            <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. PAY-2026-07" className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900" />
          </label>
          <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-300">
            Notes (optional)
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900" />
          </label>
          {formError ? <p className="text-xs text-rose-600 dark:text-rose-400">{formError}</p> : null}
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={confirm} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirm Payment
            </Button>
            <Button size="sm" variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PartnerPayoutsSection;
